import { Spinner } from '@cimplico/components';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useJournal } from '../queries/use-journal';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useFirmContext } from '../providers/FirmProvider';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsWorkpaperRole } from '../hooks/use-is-workpaper-role';
import { useState } from 'react';
import ErrorSplashScreen from './ErrorSplashScreen';
import NewOrEditJournalForm from '../features/new-or-edit-journal/NewOrEditJournalForm';
import XeroErrorAlert from '../components/XeroErrorAlert';

function EditJournalPage(): JSX.Element {
    const navigate = useNavigate();
    const { id } = useParams();
    const { shortId } = useFirmContext();
    const { isGuest } = useIsWorkpaperRole();
    const { id: workpaperId, entity } = useWorkpaperContext();
    const activeSubscription = useIsActiveSubscription();
    const [params] = useSearchParams();
    const [xeroError, setXeroError] = useState<{ name: string; message: string } | undefined>();

    function handleReturnToOverview(): void {
        navigate(`/${shortId}/workpapers/${workpaperId}/journals/${id}`);
    }

    function handleReturnToTable(): void {
        navigate(`/${shortId}/workpapers/${workpaperId}/journals${params && `?${params}`}`);
    }

    const useJournalQuery = useJournal(workpaperId, id);

    if (!activeSubscription || isGuest) {
        return <Navigate to=".." />;
    }

    if (useJournalQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (useJournalQuery.isError || !useJournalQuery.isSuccess) {
        if (useJournalQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Journal not found."
                    redirectTitle="Back to journals"
                    redirectUrl={`/${shortId}/workpapers/${workpaperId}/journals`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to journals"
                redirectUrl={`/${shortId}/workpapers/${workpaperId}/journals`}
                leftArrow={true}
            />
        );
    }

    const journal = useJournalQuery.data.journal;

    return (
        <>
            {xeroError && (
                <div className="w-fit mx-auto mb-8">
                    <XeroErrorAlert
                        message={xeroError.message}
                        errorName={xeroError.name}
                        entityName={entity?.name}
                        entityId={entity?.id}
                    />
                </div>
            )}
            <NewOrEditJournalForm
                defaultJournal={journal}
                onEdit={handleReturnToOverview}
                onCancel={handleReturnToOverview}
                onReturn={handleReturnToTable}
                onXeroError={setXeroError}
            />
        </>
    );
}

export default EditJournalPage;
